/**
 * Cart Store — Zustand
 *
 * Manages cart state with sessionStorage persistence.
 * Exports derived selectors for total and item count.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export interface CartItem {
  productId: string
  name: string
  price: number
  quantity: number
  imageUrl: string
  variantId?: string
}

interface CartStore {
  items: CartItem[]
  addItem: (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => void
  removeItem: (productId: string) => void
  updateQuantity: (productId: string, quantity: number) => void
  clearCart: () => void
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item) => {
        const existing = get().items.find((i) => i.productId === item.productId)
        if (existing) {
          set({
            items: get().items.map((i) =>
              i.productId === item.productId
                ? { ...i, quantity: i.quantity + (item.quantity ?? 1) }
                : i
            ),
          })
        } else {
          set({
            items: [...get().items, { ...item, quantity: item.quantity ?? 1 }],
          })
        }
      },

      removeItem: (productId) => {
        set({ items: get().items.filter((i) => i.productId !== productId) })
      },

      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(productId)
          return
        }
        set({
          items: get().items.map((i) =>
            i.productId === productId ? { ...i, quantity } : i
          ),
        })
      },

      clearCart: () => set({ items: [] }),
    }),
    {
      name: 'tf-cart',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)

// Derived selectors — use with useCartStore to avoid unnecessary re-renders
export const selectCartTotal = (state: CartStore): number =>
  state.items.reduce((sum, item) => sum + item.price * item.quantity, 0)

export const selectCartItemCount = (state: CartStore): number =>
  state.items.reduce((sum, item) => sum + item.quantity, 0)
